import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import AdminLayout from "@/components/layout-admin";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  LifeBuoy,
  Send,
  MessageSquare,
  Clock,
  Filter,
  Wrench,
  DollarSign,
  Plus,
  Home,
  User,
  Search,
} from "lucide-react";

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  open: { label: "Open", variant: "destructive" },
  in_progress: { label: "In Progress", variant: "secondary" },
  resolved: { label: "Resolved", variant: "default" },
  closed: { label: "Closed", variant: "outline" },
};

const priorityConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  low: { label: "Low", variant: "outline" },
  medium: { label: "Medium", variant: "secondary" },
  high: { label: "High", variant: "destructive" },
  urgent: { label: "Urgent", variant: "destructive" },
};

const categoryLabels: Record<string, string> = {
  access: "Access",
  maintenance: "Maintenance",
  cleaning_issue: "Cleaning Issue",
  billing: "Billing",
  general: "General",
};

const jobStatusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending: { label: "Pending", variant: "outline" },
  pending_approval: { label: "Awaiting Approval", variant: "destructive" },
  approved: { label: "Approved", variant: "default" },
  assigned: { label: "Assigned", variant: "secondary" },
  in_progress: { label: "In Progress", variant: "secondary" },
  completed: { label: "Completed", variant: "default" },
  cancelled: { label: "Cancelled", variant: "outline" },
};

const jobPriorityConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  low: { label: "Low", variant: "outline" },
  medium: { label: "Medium", variant: "secondary" },
  high: { label: "High", variant: "destructive" },
  urgent: { label: "Urgent", variant: "destructive" },
};

const emptyJobForm = {
  title: "",
  description: "",
  priority: "medium",
  apartmentId: "",
  assignedToUserId: "",
  ownerUserId: "",
  isBillable: false,
  costAmount: "",
  costDescription: "",
  scheduledDate: "",
};

export default function AdminTickets() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<"tickets" | "maintenance">("tickets");

  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [replyText, setReplyText] = useState("");

  const [jobFilterStatus, setJobFilterStatus] = useState<string>("all");
  const [jobFilterBillable, setJobFilterBillable] = useState<string>("all");
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [jobDetailOpen, setJobDetailOpen] = useState(false);
  const [jobCreateOpen, setJobCreateOpen] = useState(false);
  const [jobForm, setJobForm] = useState({ ...emptyJobForm });
  const [jobEditForm, setJobEditForm] = useState<any>({});

  const { data: tickets = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/tickets"],
  });

  const { data: messages = [], isLoading: messagesLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/tickets", selectedTicket?.id, "messages"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/tickets/${selectedTicket.id}/messages`);
      if (!res.ok) throw new Error("Failed to fetch messages");
      return res.json();
    },
    enabled: !!selectedTicket?.id && detailOpen,
  });

  const { data: maintenanceJobs = [], isLoading: jobsLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/maintenance-jobs"],
    enabled: activeTab === "maintenance",
  });

  const { data: users = [] } = useQuery<any[]>({
    queryKey: ["/api/admin/users"],
    enabled: activeTab === "maintenance",
  });

  const { data: apartments = [] } = useQuery<any[]>({
    queryKey: ["/api/apartments"],
    enabled: activeTab === "maintenance",
  });

  const updateTicketMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PATCH", `/api/admin/tickets/${id}`, data);
      return res.json();
    },
    onSuccess: (updatedTicket) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tickets"] });
      if (selectedTicket) {
        setSelectedTicket(updatedTicket);
      }
      toast({ title: "Ticket updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const replyMutation = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: string }) => {
      const res = await apiRequest("POST", `/api/admin/tickets/${id}/messages`, { body });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tickets", selectedTicket?.id, "messages"] });
      setReplyText("");
      toast({ title: "Reply sent" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const createJobMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/maintenance-jobs", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maintenance-jobs"] });
      setJobCreateOpen(false);
      setJobForm({ ...emptyJobForm });
      toast({ title: "Maintenance job created" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const updateJobMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PATCH", `/api/admin/maintenance-jobs/${id}`, data);
      return res.json();
    },
    onSuccess: (updatedJob) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maintenance-jobs"] });
      setSelectedJob(updatedJob);
      toast({ title: "Maintenance job updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const filteredTickets = tickets.filter((t: any) => {
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (filterPriority !== "all" && t.priority !== filterPriority) return false;
    if (filterCategory !== "all" && t.category !== filterCategory) return false;
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      const subject = (t.subject || "").toLowerCase();
      const description = (t.description || "").toLowerCase();
      if (!subject.includes(lowerSearch) && !description.includes(lowerSearch)) return false;
    }
    return true;
  });

  const filteredJobs = (maintenanceJobs as any[]).filter((j: any) => {
    if (jobFilterStatus !== "all" && j.status !== jobFilterStatus) return false;
    if (jobFilterBillable === "billable" && !j.isBillable) return false;
    if (jobFilterBillable === "non-billable" && j.isBillable) return false;
    return true;
  });

  const teamMembers = (users as any[]).filter(
    (u: any) => (u.isActive !== false) && (u.role === "cleaner" || u.role === "maintenance" || u.role === "super_admin")
  );

  const getAssigneeName = (userId: number | null) => {
    if (!userId) return "Unassigned";
    const user = (users as any[]).find((u: any) => u.id === userId);
    return user?.fullName || user?.username || `User #${userId}`;
  };

  const getApartmentName = (apartmentId: number | null) => {
    if (!apartmentId) return "";
    const apt = (apartments as any[]).find((a: any) => a.id === apartmentId);
    return apt?.name || `Apartment #${apartmentId}`;
  };

  const openTicketDetail = (ticket: any) => {
    setSelectedTicket(ticket);
    setDetailOpen(true);
    setReplyText("");
  };

  const handleReply = () => {
    if (!replyText.trim() || !selectedTicket) return;
    replyMutation.mutate({ id: selectedTicket.id, body: replyText.trim() });
  };

  const openJobDetail = (job: any) => {
    setSelectedJob(job);
    setJobEditForm({
      status: job.status || "pending",
      priority: job.priority || "medium",
      assignedToUserId: job.assignedToUserId ? String(job.assignedToUserId) : "",
      ownerUserId: job.ownerUserId ? String(job.ownerUserId) : "none",
      isBillable: job.isBillable || false,
      costAmount: job.costAmount ? String(job.costAmount) : "",
      costDescription: job.costDescription || "",
      scheduledDate: job.scheduledDate ? new Date(job.scheduledDate).toISOString().split("T")[0] : "",
    });
    setJobDetailOpen(true);
  };

  const handleJobSave = () => {
    if (!selectedJob) return;
    const data: any = {
      status: jobEditForm.status,
      priority: jobEditForm.priority,
      assignedToUserId: jobEditForm.assignedToUserId ? Number(jobEditForm.assignedToUserId) : null,
      ownerUserId: jobEditForm.ownerUserId && jobEditForm.ownerUserId !== "none" ? Number(jobEditForm.ownerUserId) : null,
      isBillable: jobEditForm.isBillable,
      costAmount: jobEditForm.isBillable && jobEditForm.costAmount ? Number(jobEditForm.costAmount) : null,
      costDescription: jobEditForm.isBillable ? jobEditForm.costDescription : null,
      scheduledDate: jobEditForm.scheduledDate || null,
    };
    updateJobMutation.mutate({ id: selectedJob.id, data });
  };

  const handleCreateJob = () => {
    if (!jobForm.title.trim()) {
      toast({ title: "Error", description: "Title is required", variant: "destructive" });
      return;
    }
    const data: any = {
      title: jobForm.title,
      description: jobForm.description,
      priority: jobForm.priority,
      apartmentId: jobForm.apartmentId ? Number(jobForm.apartmentId) : null,
      assignedToUserId: jobForm.assignedToUserId ? Number(jobForm.assignedToUserId) : null,
      ownerUserId: jobForm.ownerUserId && jobForm.ownerUserId !== "none" ? Number(jobForm.ownerUserId) : null,
      isBillable: jobForm.isBillable,
      costAmount: jobForm.isBillable && jobForm.costAmount ? Number(jobForm.costAmount) : null,
      costDescription: jobForm.isBillable ? jobForm.costDescription : null,
      scheduledDate: jobForm.scheduledDate || null,
    };
    createJobMutation.mutate(data);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-tickets-heading">Support & Maintenance</h1>
          <p className="text-sm text-muted-foreground">Manage tickets and maintenance jobs</p>
        </div>

        <div className="flex gap-2 flex-wrap" data-testid="tabs-main">
          <Button
            variant={activeTab === "tickets" ? "default" : "outline"}
            onClick={() => setActiveTab("tickets")}
            data-testid="tab-tickets"
          >
            <LifeBuoy className="w-4 h-4 mr-2" />
            Tickets ({tickets.length})
          </Button>
          <Button
            variant={activeTab === "maintenance" ? "default" : "outline"}
            onClick={() => setActiveTab("maintenance")}
            data-testid="tab-maintenance"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Maintenance Jobs ({maintenanceJobs.length})
          </Button>
        </div>

        {activeTab === "tickets" && (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search subject, description..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8" data-testid="search-input" />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]" data-testid="select-filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-[140px]" data-testid="select-filter-priority">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[140px]" data-testid="select-filter-category">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="access">Access</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="cleaning_issue">Cleaning Issue</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-24 w-full rounded-md" />
                ))}
              </div>
            ) : filteredTickets.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground" data-testid="text-no-tickets">
                  <LifeBuoy className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No tickets found matching your filters.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredTickets.map((ticket: any) => {
                  const sc = statusConfig[ticket.status] || statusConfig.open;
                  const pc = priorityConfig[ticket.priority] || priorityConfig.medium;
                  return (
                    <Card
                      key={ticket.id}
                      className="hover-elevate cursor-pointer"
                      onClick={() => openTicketDetail(ticket)}
                      data-testid={`card-ticket-${ticket.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1 space-y-1">
                            <p className="font-medium truncate" data-testid={`text-ticket-subject-${ticket.id}`}>
                              {ticket.subject}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                              <span data-testid={`text-ticket-category-${ticket.id}`}>
                                {categoryLabels[ticket.category] || ticket.category}
                              </span>
                              <span>
                                <Clock className="w-3 h-3 inline mr-0.5" />
                                {new Date(ticket.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" })}
                              </span>
                              {ticket.guestName && (
                                <span data-testid={`text-ticket-creator-${ticket.id}`}>
                                  by {ticket.guestName}
                                </span>
                              )}
                              {ticket.createdByUserId && !ticket.guestName && (
                                <span data-testid={`text-ticket-creator-${ticket.id}`}>
                                  User #{ticket.createdByUserId}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge variant={pc.variant} className="no-default-active-elevate" data-testid={`badge-ticket-priority-${ticket.id}`}>
                              {pc.label}
                            </Badge>
                            <Badge variant={sc.variant} className="no-default-active-elevate" data-testid={`badge-ticket-status-${ticket.id}`}>
                              {sc.label}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === "maintenance" && (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <Select value={jobFilterStatus} onValueChange={setJobFilterStatus}>
                  <SelectTrigger className="w-[160px]" data-testid="select-job-filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="pending_approval">Awaiting Approval</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={jobFilterBillable} onValueChange={setJobFilterBillable}>
                  <SelectTrigger className="w-[160px]" data-testid="select-job-filter-billable">
                    <SelectValue placeholder="Billable" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="billable">Billable</SelectItem>
                    <SelectItem value="non-billable">Non-billable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => { setJobForm({ ...emptyJobForm }); setJobCreateOpen(true); }} data-testid="button-new-job">
                <Plus className="w-4 h-4 mr-2" />
                New Job
              </Button>
            </div>

            {jobsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-24 w-full rounded-md" />
                ))}
              </div>
            ) : filteredJobs.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground" data-testid="text-no-jobs">
                  <Wrench className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No maintenance jobs found.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredJobs.map((job: any) => {
                  const sc = jobStatusConfig[job.status] || jobStatusConfig.pending;
                  const pc = jobPriorityConfig[job.priority] || jobPriorityConfig.medium;
                  return (
                    <Card
                      key={job.id}
                      className="hover-elevate cursor-pointer"
                      onClick={() => openJobDetail(job)}
                      data-testid={`card-job-${job.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1 space-y-1">
                            <p className="font-medium truncate" data-testid={`text-job-title-${job.id}`}>
                              {job.title}
                            </p>
                            {job.description && (
                              <p className="text-sm text-muted-foreground truncate" data-testid={`text-job-description-${job.id}`}>
                                {job.description}
                              </p>
                            )}
                            <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                              <span className="flex items-center gap-1" data-testid={`text-job-assignee-${job.id}`}>
                                <User className="w-3 h-3" />
                                {getAssigneeName(job.assignedToUserId)}
                              </span>
                              {job.apartmentId && (
                                <span className="flex items-center gap-1" data-testid={`text-job-apartment-${job.id}`}>
                                  <Home className="w-3 h-3" />
                                  {getApartmentName(job.apartmentId)}
                                </span>
                              )}
                              {job.ticketSubject && (
                                <span data-testid={`text-job-ticket-${job.id}`}>
                                  Ticket: {job.ticketSubject}
                                </span>
                              )}
                              {job.ownerUserId && (
                                <span data-testid={`text-job-owner-${job.id}`}>
                                  PM: {getAssigneeName(job.ownerUserId)}
                                </span>
                              )}
                              {job.isBillable && (
                                <span className="flex items-center gap-1" data-testid={`text-job-billable-${job.id}`}>
                                  <DollarSign className="w-3 h-3" />
                                  {job.costAmount ? `$${Number(job.costAmount).toFixed(2)}` : "Billable"}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge variant={pc.variant} className="no-default-active-elevate" data-testid={`badge-job-priority-${job.id}`}>
                              {pc.label}
                            </Badge>
                            <Badge variant={sc.variant} className="no-default-active-elevate" data-testid={`badge-job-status-${job.id}`}>
                              {sc.label}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={detailOpen} onOpenChange={(open) => { setDetailOpen(open); if (!open) setSelectedTicket(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle data-testid="text-ticket-detail-subject">{selectedTicket?.subject}</DialogTitle>
            <DialogDescription>
              {categoryLabels[selectedTicket?.category] || selectedTicket?.category} &middot; Created {selectedTicket?.createdAt ? new Date(selectedTicket.createdAt).toLocaleString() : ""}
            </DialogDescription>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-4 flex-1 overflow-y-auto">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Select
                    value={selectedTicket.status}
                    onValueChange={(v) => updateTicketMutation.mutate({ id: selectedTicket.id, data: { status: v } })}
                  >
                    <SelectTrigger className="w-[140px]" data-testid="select-ticket-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Priority</Label>
                  <Select
                    value={selectedTicket.priority}
                    onValueChange={(v) => updateTicketMutation.mutate({ id: selectedTicket.id, data: { priority: v } })}
                  >
                    <SelectTrigger className="w-[140px]" data-testid="select-ticket-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm font-semibold">Messages</Label>
                </div>
                {messagesLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="text-no-messages">No messages yet.</p>
                ) : (
                  <div className="space-y-3 max-h-[40vh] overflow-y-auto">
                    {messages.map((msg: any) => (
                      <Card key={msg.id} data-testid={`card-message-${msg.id}`}>
                        <CardContent className="p-3 space-y-1">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="text-sm font-medium" data-testid={`text-message-sender-${msg.id}`}>
                              {msg.senderName || `User #${msg.senderUserId}` || "System"}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(msg.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm" data-testid={`text-message-body-${msg.id}`}>{msg.body}</p>
                          {msg.attachmentUrls && (msg.attachmentUrls as string[]).length > 0 && (
                            <div className="flex gap-2 flex-wrap mt-2">
                              {(msg.attachmentUrls as string[]).map((url: string, idx: number) => (
                                <a key={idx} href={url} target="_blank" rel="noopener noreferrer" data-testid={`ticket-attachment-${msg.id}-${idx}`}>
                                  <img
                                    src={url}
                                    alt={`Attachment ${idx + 1}`}
                                    className="w-20 h-20 object-cover rounded-md border cursor-pointer hover:ring-2 ring-primary transition-all"
                                  />
                                </a>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-2 border-t">
                <Label className="text-sm">Reply</Label>
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply..."
                  className="resize-none"
                  data-testid="input-reply"
                />
                <Button
                  onClick={handleReply}
                  disabled={!replyText.trim() || replyMutation.isPending}
                  data-testid="button-send-reply"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {replyMutation.isPending ? "Sending..." : "Send Reply"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={jobDetailOpen} onOpenChange={(open) => { setJobDetailOpen(open); if (!open) setSelectedJob(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle data-testid="text-job-detail-title">{selectedJob?.title}</DialogTitle>
            <DialogDescription>
              {selectedJob?.description || "No description"}
            </DialogDescription>
          </DialogHeader>

          {selectedJob && (
            <div className="space-y-4 flex-1 overflow-y-auto">
              {selectedJob.ticketId && selectedJob.ticketSubject && (
                <div data-testid="text-job-linked-ticket">
                  <Label className="text-xs text-muted-foreground">Linked Ticket</Label>
                  <button
                    className="block text-sm text-primary underline cursor-pointer"
                    onClick={() => {
                      setJobDetailOpen(false);
                      const ticket = tickets.find((t: any) => t.id === selectedJob.ticketId);
                      if (ticket) openTicketDetail(ticket);
                    }}
                    data-testid="link-job-ticket"
                  >
                    {selectedJob.ticketSubject}
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Select
                    value={jobEditForm.status}
                    onValueChange={(v) => setJobEditForm((p: any) => ({ ...p, status: v }))}
                  >
                    <SelectTrigger data-testid="select-job-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="pending_approval">Awaiting Approval</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Priority</Label>
                  <Select
                    value={jobEditForm.priority}
                    onValueChange={(v) => setJobEditForm((p: any) => ({ ...p, priority: v }))}
                  >
                    <SelectTrigger data-testid="select-job-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Assign To</Label>
                <Select
                  value={jobEditForm.assignedToUserId || "unassigned"}
                  onValueChange={(v) => setJobEditForm((p: any) => ({ ...p, assignedToUserId: v === "unassigned" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-job-assignee">
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {teamMembers.map((u: any) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.fullName || u.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Property Manager</Label>
                <Select
                  value={jobEditForm.ownerUserId || "none"}
                  onValueChange={(v) => setJobEditForm((p: any) => ({ ...p, ownerUserId: v }))}
                >
                  <SelectTrigger data-testid="select-job-owner">
                    <SelectValue placeholder="Select property manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Property Manager</SelectItem>
                    {(users as any[]).filter((u: any) => u.role === "owner" && u.isActive !== false).map((u: any) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.fullName || u.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="job-billable"
                    checked={jobEditForm.isBillable}
                    onCheckedChange={(checked) => setJobEditForm((p: any) => ({ ...p, isBillable: !!checked }))}
                    data-testid="checkbox-job-billable"
                  />
                  <Label htmlFor="job-billable" className="text-sm cursor-pointer">This job has a cost</Label>
                </div>
                {jobEditForm.isBillable && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Cost Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={jobEditForm.costAmount}
                        onChange={(e) => setJobEditForm((p: any) => ({ ...p, costAmount: e.target.value }))}
                        placeholder="0.00"
                        data-testid="input-job-cost-amount"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Cost Description</Label>
                      <Input
                        value={jobEditForm.costDescription}
                        onChange={(e) => setJobEditForm((p: any) => ({ ...p, costDescription: e.target.value }))}
                        placeholder="e.g. Parts and labor"
                        data-testid="input-job-cost-description"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Scheduled Date</Label>
                <Input
                  type="date"
                  value={jobEditForm.scheduledDate}
                  onChange={(e) => setJobEditForm((p: any) => ({ ...p, scheduledDate: e.target.value }))}
                  data-testid="input-job-scheduled-date"
                />
              </div>

              <Button
                onClick={handleJobSave}
                disabled={updateJobMutation.isPending}
                className="w-full"
                data-testid="button-save-job"
              >
                {updateJobMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={jobCreateOpen} onOpenChange={(open) => { setJobCreateOpen(open); if (!open) setJobForm({ ...emptyJobForm }); }}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle data-testid="text-create-job-title">New Maintenance Job</DialogTitle>
            <DialogDescription>Create a new maintenance job</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 flex-1 overflow-y-auto">
            <div className="space-y-1">
              <Label>Title</Label>
              <Input
                value={jobForm.title}
                onChange={(e) => setJobForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="Job title"
                data-testid="input-create-job-title"
              />
            </div>

            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea
                value={jobForm.description}
                onChange={(e) => setJobForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Describe the job..."
                className="resize-none"
                data-testid="input-create-job-description"
              />
            </div>

            <div className="space-y-1">
              <Label>Priority</Label>
              <Select value={jobForm.priority} onValueChange={(v) => setJobForm((p) => ({ ...p, priority: v }))}>
                <SelectTrigger data-testid="select-create-job-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Apartment</Label>
              <Select value={jobForm.apartmentId} onValueChange={(v) => {
                const selectedApt = (apartments as any[]).find((a: any) => a.id === Number(v));
                setJobForm((p) => ({
                  ...p,
                  apartmentId: v,
                  ownerUserId: selectedApt?.ownerUserId ? String(selectedApt.ownerUserId) : "none",
                }));
              }}>
                <SelectTrigger data-testid="select-create-job-apartment">
                  <SelectValue placeholder="Select apartment" />
                </SelectTrigger>
                <SelectContent>
                  {(apartments as any[]).map((a: any) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Property Manager</Label>
              <Select value={jobForm.ownerUserId} onValueChange={(v) => setJobForm((p) => ({ ...p, ownerUserId: v }))}>
                <SelectTrigger data-testid="select-create-job-owner">
                  <SelectValue placeholder="Select property manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Property Manager</SelectItem>
                  {(users as any[]).filter((u: any) => u.role === "owner" && u.isActive !== false).map((u: any) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.fullName || u.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Auto-filled from apartment.</p>
            </div>

            <div className="space-y-1">
              <Label>Assign To</Label>
              <Select value={jobForm.assignedToUserId} onValueChange={(v) => setJobForm((p) => ({ ...p, assignedToUserId: v }))}>
                <SelectTrigger data-testid="select-create-job-assignee">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamMembers.map((u: any) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.fullName || u.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="create-job-billable"
                  checked={jobForm.isBillable}
                  onCheckedChange={(checked) => setJobForm((p) => ({ ...p, isBillable: !!checked }))}
                  data-testid="checkbox-create-job-billable"
                />
                <Label htmlFor="create-job-billable" className="text-sm cursor-pointer">This job has a cost</Label>
              </div>
              {jobForm.isBillable && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Cost Amount ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={jobForm.costAmount}
                      onChange={(e) => setJobForm((p) => ({ ...p, costAmount: e.target.value }))}
                      placeholder="0.00"
                      data-testid="input-create-job-cost-amount"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Cost Description</Label>
                    <Input
                      value={jobForm.costDescription}
                      onChange={(e) => setJobForm((p) => ({ ...p, costDescription: e.target.value }))}
                      placeholder="e.g. Parts and labor"
                      data-testid="input-create-job-cost-description"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label>Scheduled Date</Label>
              <Input
                type="date"
                value={jobForm.scheduledDate}
                onChange={(e) => setJobForm((p) => ({ ...p, scheduledDate: e.target.value }))}
                data-testid="input-create-job-scheduled-date"
              />
            </div>

            <Button
              onClick={handleCreateJob}
              disabled={createJobMutation.isPending}
              className="w-full"
              data-testid="button-create-job"
            >
              {createJobMutation.isPending ? "Creating..." : "Create Job"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
